import React, { FC } from 'react'
import styled, { css, StyledComponentPropsWithRef } from 'styled-components'
import Icon, { IconSize } from 'uiComponents/atoms/Icon/Icon'
import { IconName } from 'uiComponents/atoms/Icon/IconName'
import {
  bordersColor,
  complementaryColor,
  inactiveColor,
  primaryColor,
} from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXS,
  fwMediumBold,
} from 'uiComponents/constants/typography'

const Label = styled.div`
  flex: 1 0 auto;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 10px;
  border-right: 1px solid ${bordersColor};
`

const DropdownIcon = styled.div`
  position: absolute;
  right: 10px;
  top: calc(50% - 4px);
`

const StyledIcon = styled(Icon)`
  display: block;
  width: 6px;
  opacity: 0.3;
  opacity: var(--dropdown-icon-opacity, 0.3);
`

const StyledDropdownButton = styled.button<{
  selected?: boolean
  isOpen?: boolean
}>`
  display: flex;
  width: 100%;
  height: 100%;
  border: 1px solid ${bordersColor};
  border: var(--dropdown-border, 1px) solid
    var(--dropdown-color, ${bordersColor});
  border-radius: 2px;
  line-height: 46px;
  cursor: pointer;
  ${fontFamilyPrimary};
  font-size: ${fsXS}px;
  ${fwMediumBold};
  background-color: ${complementaryColor};

  ${props =>
    props.isOpen &&
    css`
      ${openState};
      ${activeState};
    `};

  ${props =>
    (props.selected || props.isOpen) &&
    css`
      ${activeState};
    `};
`

const Value = styled.div`
  display: flex;
  align-items: center;
  height: 46px;
  padding: 0 25px 0 15px;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${inactiveColor};
  color: var(--dropdown-color, ${inactiveColor});
`

const activeState = css`
  ${Value} {
    color: ${primaryColor};
  }
`

const openState = css`
  border: 1.5px solid ${primaryColor};
  > ${DropdownIcon} > ${StyledIcon} {
    transform: rotateX(180deg);
    opacity: 1;
  }
  > ${Label} {
    border-right: 1.5px solid ${primaryColor};
  }
`

interface Props extends Omit<StyledComponentPropsWithRef<'button'>, 'ref'> {
  label?: React.ReactNode
  className?: string
  selected?: boolean
  isOpen?: boolean
}

const DropDownButton: FC<Props> = ({
  label,
  className,
  children,
  selected,
  isOpen,
  ...props
}) => {
  return (
    <StyledDropdownButton
      data-testid="SingleSizeDropdownButton"
      selected={selected}
      isOpen={isOpen}
      className={className}
      {...props}
    >
      {label && <Label>{label}</Label>}
      <Value data-testid="Value">{children}</Value>
      <DropdownIcon>
        <StyledIcon size={IconSize.XS} asset={IconName.IcDropdownClosed} />
      </DropdownIcon>
    </StyledDropdownButton>
  )
}

export default DropDownButton
